'use client'

import {ChevronDown, ChevronRight} from 'lucide-react'
import {useEffect, useMemo, useRef, useState} from 'react'
import {cn, tv} from 'tailwind-variants'

import {Link, useLocation} from 'react-router-dom'
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from 'src/components/collapsible'
import {HoverCard, HoverCardContent, HoverCardTrigger} from 'src/components/hover-card'
import useWindowSize from 'src/components/slider/hooks/useWindowSize'
import {NavigationMenuType} from 'src/layouts/main-layout/app-sidebar/navigation/types/navigation-menu.type'
import {isPathActive} from 'src/layouts/main-layout/app-sidebar/utils/is-path-active.helper'
import {useAppSidebar} from './app-sidebar-context'
import {AppSidebarNavIcon} from './app-sidebar-nav-icon'
import {AppSidebarSubnav} from './app-sidebar-subnav'

interface Props {
  navigationMenu: NavigationMenuType
}

const navItemVariants = tv({
  base: [
    'group flex w-full items-center gap-3 h-10',
    'rounded-xl',
    'px-3 py-2',
    'text-sm font-medium',
    'transition-colors',
    'min-w-0 overflow-hidden',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-[#182B4466]',
    'hover:rounded-[5px_0_0_5px]',
  ],
  variants: {
    active: {
      true: 'bg-sidebar-accent text-sidebar-foreground font-bold hover:bg-sidebar-accent hover:rounded-[5px_0_0_5px]',
      false: 'text-sidebar-foreground font-normal',
    },
    collapsed: {
      true: 'justify-center',
      false: 'justify-between',
    },
  },
})

export function AppSidebarNavItem({navigationMenu}: Props) {
  const [open, setOpen] = useState(false)
  const [hoverCardOpen, setHoverCardOpen] = useState(false)
  const canOpenHoverCard = useRef(false)

  const {collapsed, toggleCollapsed} = useAppSidebar()
  const {pathname} = useLocation()
  const {width} = useWindowSize()
  const mobileWidth = width <= 767

  const active = useMemo(() => {
    const selfActive = isPathActive(pathname, navigationMenu.route || '')

    const childActive = navigationMenu.children?.some((child) => isPathActive(pathname, child.route || '')) ?? false

    return selfActive || childActive
  }, [pathname, navigationMenu])

  useEffect(() => {
    if (collapsed) setOpen(false)
    else if (active) setOpen(true)
  }, [collapsed, active])

  useEffect(() => {
    canOpenHoverCard.current = false

    if (!collapsed) setHoverCardOpen(false)
  }, [collapsed, pathname])

  const hasChildren = Boolean(navigationMenu.children?.length)
  const closeTimeout = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current)
    }
  }, [])

  const handleOnMouseEnter = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current)
    if (!collapsed && hasChildren) setOpen(true)
  }

  const handleOnMouseLeave = () => {
    if (!collapsed && !active) {
      closeTimeout.current = setTimeout(() => {
        setOpen(false)
      }, 1020)
    }
  }

  const handleHoverCardOpenChange = (nextOpen: boolean) => {
    if (!collapsed) {
      setHoverCardOpen(false)
      return
    }
    if (nextOpen && !canOpenHoverCard.current) return
    setHoverCardOpen(nextOpen)
  }

  return (
    <HoverCard open={!collapsed ? false : hoverCardOpen} onOpenChange={handleHoverCardOpenChange} openDelay={1} closeDelay={1}>
      <Collapsible open={open} onOpenChange={setOpen} onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
        <CollapsibleTrigger asChild>
          <HoverCardTrigger asChild>
            <Link to={navigationMenu.route ? navigationMenu.route : navigationMenu.children?.[0]?.route || '#'} style={{textDecoration: 'none'}}>
              <button
                type='button'
                data-slot='sidebar-item'
                data-active={active ? active : undefined}
                onClick={() => mobileWidth && toggleCollapsed()}
                onPointerEnter={() => {
                  if (collapsed) canOpenHoverCard.current = true
                }}
                onPointerLeave={() => {
                  canOpenHoverCard.current = false
                }}
                className={cn(navItemVariants({active, collapsed}), collapsed && hoverCardOpen ? 'rounded-[5px_0_0_5px]' : 'rounded-[5px]')}>
                <span className={cn('shrink-0', collapsed && 'flex  w-[18px] items-center justify-center')}>
                  <AppSidebarNavIcon icon={navigationMenu.icon} active={active} />
                </span>

                {!collapsed && (
                  <span className='flex min-w-0 flex-1 items-center justify-between overflow-hidden'>
                    <span className='min-w-0 flex-1 truncate text-left'>{navigationMenu.label}</span>

                    {hasChildren &&
                      (open ? (
                        <ChevronDown className='text-sidebar-foreground size-4 shrink-0' />
                      ) : (
                        <ChevronRight className='text-sidebar-foreground size-4 shrink-0' />
                      ))}
                  </span>
                )}
              </button>
            </Link>
          </HoverCardTrigger>
        </CollapsibleTrigger>
        {hasChildren && !collapsed && (
          <CollapsibleContent
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
            className='data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden transition-all'>
            <AppSidebarSubnav
              items={
                navigationMenu.children.map((child) => ({
                  id: child.id,
                  label: child.label,
                  route: child.route,
                })) || []
              }
            />
          </CollapsibleContent>
        )}
      </Collapsible>

      {navigationMenu.children.length > 0 && collapsed && (
        <HoverCardContent
          side='right'
          className='bg-sidebar-accent border-sidebar-accent p-0 pt-1 pb-5'
          onPointerLeave={() => setHoverCardOpen(false)}
          onEscapeKeyDown={() => setHoverCardOpen(false)}>
          <div className='flex h-10 items-center px-6'>
            <h3 className='text-base font-bold text-[#FCFCFDE5]'>{navigationMenu.label}</h3>
          </div>
          {navigationMenu.children.length > 0 && (
            <AppSidebarSubnav
              items={
                navigationMenu.children.map((child) => ({
                  id: child.id,
                  label: child.label,
                  route: child.route,
                })) || []
              }
            />
          )}
        </HoverCardContent>
      )}
    </HoverCard>
  )
}
